import type React from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FeatureCard } from '../components/feature-card';
import { PublicFooter } from '../components/public-footer';
import { PublicHeader } from '../components/public-header';
import { Button } from '../components/ui/button';
import { useFeatureFlag } from '../contexts/feature-flag-context';
import { useKeycloakAuth } from '../contexts/keycloak-auth-context';

const TypingAnimation: React.FC = () => {
  const phrases = [
    'streamline your payments',
    'manage cash flow with ease',
    'automate your finances',
    'gain real-time insights',
    'transform your business',
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseDuration = 2000;

    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseDuration);
      return () => clearTimeout(pauseTimer);
    }

    if (!isDeleting && displayedText === currentPhrase) {
      setIsPaused(true);
      return;
    }

    if (isDeleting && displayedText === '') {
      setIsDeleting(false);
      setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
      return;
    }

    const timer = setTimeout(() => {
      if (isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, isPaused, currentPhraseIndex]);

  return (
    <span className="inline-flex items-center">
      <span className="text-primary font-semibold">{displayedText}</span>
      <span className="ml-1 w-1 h-10 bg-primary animate-pulse" />
    </span>
  );
};

export const Home: React.FC = () => {
  const { isAuthenticated, isLoading, isInitialized, login } = useKeycloakAuth();
  const registrationEnabled = useFeatureFlag('REGISTRATION_ENABLED');
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect authenticated users to dashboard or their intended destination
  useEffect(() => {
    if (isAuthenticated && !isLoading && isInitialized) {
      // Check if there's a saved destination from protected route
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
      const destination = from && from !== '/' ? from : '/dashboard';

      // Use setTimeout to ensure auth state is fully synced
      const timer = setTimeout(() => {
        navigate(destination, { replace: true });
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [isAuthenticated, isLoading, isInitialized, navigate, location]);

  // Don't block UI if Keycloak initialization takes too long
  // Show loading only if still initializing and hasn't been too long
  const [initTimeout, setInitTimeout] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isInitialized) {
        setInitTimeout(true);
      }
    }, 5000); // After 5 seconds, stop showing loading
    return () => clearTimeout(timer);
  }, [isInitialized]);

  if ((!isInitialized || isLoading) && !initTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex flex-col">
      {/* Modern Header */}
      <PublicHeader onSignIn={login} />

      {/* Hero Section */}
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="text-center max-w-4xl">
          {/* Main Heading with Typing Effect */}
          <div className="mb-6">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-4 leading-tight">
              Built to
            </h1>
            <div className="min-h-[4rem] flex items-center justify-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <TypingAnimation />
              </h1>
            </div>
          </div>

          {/* Subtitle */}
          <p className="text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
            The financial operations platform designed for modern businesses. Manage payments, track
            cash flow, and make data-driven decisions—all in one place.
          </p>

          {/* CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={login}
              size="lg"
              className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
            >
              Sign In
            </Button>
            {registrationEnabled && (
              <Button
                onClick={() => navigate('/register')}
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 border-2 border-gray-300 text-primary hover:bg-primary/5"
              >
                Register
              </Button>
            )}
          </div>

          {/* Feature Highlights */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <FeatureCard
              icon={
                <svg
                  aria-label="Lightning bolt icon"
                  className="w-6 h-6 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <title>Lightning bolt icon</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              }
              title="Lightning Fast"
              description="Process payments and reconcile accounts in real-time with automated workflows."
            />

            <FeatureCard
              icon={
                <svg
                  aria-label="Shield with checkmark icon"
                  className="w-6 h-6 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <title>Shield with checkmark icon</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              }
              title="Secure by Design"
              description="Enterprise-grade security with role-based access and comprehensive audit trails."
            />

            <FeatureCard
              icon={
                <svg
                  className="w-6 h-6 text-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-label="Bar chart icon"
                >
                  <title>Bar chart icon</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              }
              title="Data-Driven"
              description="Make informed decisions with powerful analytics and real-time financial insights."
            />
          </div>
        </div>
      </main>

      {/* Modern Footer */}
      <PublicFooter />
    </div>
  );
};
